import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const BASE_TITLE = "Profio";
const DEFAULT_TITLE = "Profio — Find Specialists You Can Trust";

/**
 * Sets a per-page document.title on every route change.
 * Every page used to share one static title, which hurts SEO,
 * browser history and bookmarks.
 *
 * /tutor/:id is intentionally skipped — TutorProfilePublic.jsx sets its own
 * "<Specialist name> | Profio" title after it loads the profile.
 */
const TITLES = {
  "/":                { uk: null, en: null }, // homepage keeps the default marketing title
  "/about":           { uk: "Про нас", en: "About us" },
  "/contact":         { uk: "Контакти", en: "Contact" },
  "/privacy":         { uk: "Політика конфіденційності", en: "Privacy Policy" },
  "/tos":             { uk: "Умови користування", en: "Terms of Service" },
  "/cookies":         { uk: "Політика щодо cookie", en: "Cookie Policy" },
  "/faq":             { uk: "Часті запитання", en: "FAQ" },
  "/login":           { uk: "Вхід", en: "Sign in" },
  "/signup":          { uk: "Реєстрація", en: "Sign up" },
  "/forgot-password": { uk: "Відновлення пароля", en: "Password recovery" },
  "/search":          { uk: "Пошук спеціалістів", en: "Find specialists" },
  "/dashboard":       { uk: "Дашборд", en: "Dashboard" },
  "/account":         { uk: "Налаштування акаунта", en: "Account settings" },
  "/messages":        { uk: "Повідомлення", en: "Messages" },
  "/bookings":        { uk: "Мої бронювання", en: "My bookings" },
  "/my-requests":     { uk: "Мої запити", en: "My requests" },
  "/transactions":    { uk: "Транзакції", en: "Transactions" },
};

export default function PageTitle() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const path = location.pathname;

    // Pages that manage their own title (dynamic data).
    if (path.startsWith("/tutor/")) return;

    const isUk = (i18n.resolvedLanguage || i18n.language || "uk").startsWith("uk");
    const entry =
      TITLES[path] ||
      // Match nested paths like /dashboard/xyz or /messages/123
      TITLES[Object.keys(TITLES).find((k) => k !== "/" && path.startsWith(k + "/")) || ""];

    const label = entry ? (isUk ? entry.uk : entry.en) : null;
    document.title = label ? `${label} | ${BASE_TITLE}` : DEFAULT_TITLE;
  }, [location.pathname, i18n.resolvedLanguage, i18n.language]);

  return null;
}
